import React, { useEffect, useState } from "react";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";

import { useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";

import { firebaseAuth } from "../../../components/firebase/auth";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  pdf,
  Font,
} from "@react-pdf/renderer";
// @ts-ignore
import { saveAs } from "file-saver";
import moment from "moment";
// @ts-ignore
import QRCode from "qrcode";
// Barcode generator will be dynamically imported in createPdf

import {
  PlantArrayIF,
  LabelArrayIF,
  PlantInfoIF,
} from "../../../components/interfaces/Interfaces";
import { firebaseStore } from "../../../components/firebase/firestore";

import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import Button from "@mui/material/Button";
import "../dashboard.css";
import "./labels.css";
import "../batchWorkspace.css";
import "../weeklyPlanning.css";
import "./labelWorkspace.css";
import Autocomplete from "@mui/material/Autocomplete";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LocalPrintshopOutlinedIcon from "@mui/icons-material/LocalPrintshopOutlined";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
// @ts-ignore
import PapyrusFont from "../../../static/fonts/papyrus.ttf";
// @ts-ignore
import NoteworthyFont from "../../../static/fonts/noteworthy-bold.ttf";
// @ts-ignore
import RobotoItalicFont from "../../../static/fonts/Roboto-Italic.ttf";
// @ts-ignore
import RobotoBoldFont from "../../../static/fonts/Roboto-Bold.ttf";

/**
 * Helper: Build the flattened plant/menu array used by the select menu.
 * Returns an array where each entry represents a single product-size combination.
 */
interface FirestoreFreshProduct {
  id: string;
  name_sv: string;
  name_en: string;
  icon: string;
  logo?: string;
  url: string;
  urlArr: string[];
  subtitle_sv?: string;
}

interface FirestoreFreshPackage {
  id: string;
  parentId: string;
  size: number;
  formatedSize: string;
  isBox?: boolean;
  active?: boolean;
  boundBagPackageId?: string;
  ean?: string;
  menuName_sv?: string;
}

const isActiveFirestorePackage = (packageItem: FirestoreFreshPackage, packages: FirestoreFreshPackage[]): boolean => {
  if (packageItem.active === false) return false;
  if (!packageItem.boundBagPackageId) return true;
  return packages.find((candidate) => candidate.id === packageItem.boundBagPackageId)?.active !== false;
};

const buildMenuArray = (products: FirestoreFreshProduct[], packages: FirestoreFreshPackage[]): PlantArrayIF => {
  const tempArr: PlantArrayIF = [];
  packages.forEach((packageItem) => {
    if (!isActiveFirestorePackage(packageItem, packages)) return;
    const product = products.find((candidate) => candidate.id === packageItem.parentId);
    if (!product) return;
    const labelName = packageItem.menuName_sv || `${product.name_sv} - ${packageItem.formatedSize || `${packageItem.size}g`}`;
    tempArr.push({
      name: [product.name_sv, product.name_en],
      formatedName: product.name_sv,
      id: `${product.id}-${packageItem.id}`,
      link: product.id,
      tray: 0,
      size: [packageItem.size],
      ean: [packageItem.ean || ""],
      storeData: ["", ""],
      leadTime: 0,
      storeTemp: 0,
      water: 0,
      shelfLife: "",
      emissionFactor: 0,
      rateStore: 0,
      rateTaste: 0,
      rateSpicy: 0,
      icon: product.icon || "",
      logo: product.logo || "",
      url: product.url || "",
      urlArr: product.urlArr || [],
      subtitle: [product.subtitle_sv || "", ""],
      menuName: [[labelName, labelName]],
      formatedMenuName: labelName,
      formatedSize: [packageItem.formatedSize || `${packageItem.size}g`],
      formatedSizeString: packageItem.formatedSize || `${packageItem.size}g`,
      isBox: [packageItem.isBox === true],
      color: ["", ""],
      flavor: ["", ""],
      texture: ["", ""],
      description: [],
      kitchen: [],
      nutrientList: [],
      cool: "",
      ec: "",
      hum: "",
      light: "",
      ph: "",
      pump: "",
      spray: "",
      temp: "",
      waterTemp: "",
      type: packageItem.isBox ? "box" : "bag",
      mustard: false,
      labelSize: packageItem.size,
      labelEAN: packageItem.ean || "",
    });
  });
  tempArr.sort((a, b) => a.formatedMenuName?.localeCompare(b.formatedMenuName || "") || 0);
  return tempArr;
};

/**
 * Generate a QR data URL for a given URL string using the QRCode library.
 */
export const generateQrDataUrl = async (url: string): Promise<string> => {
  // QRCode is used via its default exported API (browser-friendly)
  // Keep the existing options for size and correction level
  // @ts-ignore - QRCode library types are not included in this project
  return await QRCode.toDataURL(url, { errorCorrectionLevel: "M", margin: 2, width: 100 });
};

/**
 * Generate a barcode image data URL for the provided EAN value.
 * Dynamically imports JsBarcode and attempts EAN13 then falls back to CODE128.
 */
export const generateBarcodeDataUrl = async (eanValue: string): Promise<string | null> => {
  if (!eanValue) return null;
  try {
    // Dynamically import JsBarcode to avoid bundling it at module load.
    // @ts-ignore
    const JsBarcodeModule = await import("jsbarcode");
    // JsBarcode may be the default export or the module itself
    // @ts-ignore
    const JsBarcode = JsBarcodeModule && (JsBarcodeModule.default || JsBarcodeModule);
    if (typeof document === "undefined" || !JsBarcode) return null;
    const canvas = document.createElement("canvas");
    try {
      JsBarcode(canvas, eanValue.toString(), { format: "EAN13", displayValue: true, height: 80, margin: 0 });
      return canvas.toDataURL("image/png");
    } catch (err) {
      try {
        JsBarcode(canvas, eanValue.toString(), { format: "CODE128", displayValue: true, height: 80, margin: 0 });
        return canvas.toDataURL("image/png");
      } catch (err2) {
        console.warn("Barcode generation failed for", eanValue, err2);
        return null;
      }
    }
  } catch (e) {
    console.warn("Could not load JsBarcode for barcode generation", e);
    return null;
  }
};


Font.register({
  family: "Papyrus",
  fonts: [
    {
      src: PapyrusFont,
    },
  ],
});
Font.register({
  family: "Noteworthy",
  fonts: [
    {
      src: NoteworthyFont,
    },
  ],
});
Font.register({
  family: "Roboto-Italic",
  fonts: [
    {
      src: RobotoItalicFont,
    },
  ],
});
Font.register({
  family: "Roboto-Bold",
  fonts: [
    {
      src: RobotoBoldFont,
    },
  ],
});
export const styles = StyleSheet.create({
  page: {
    flexDirection: "row",
    flexWrap: "wrap",
    backgroundColor: "#fff",
  },
  section: {
    height: "50%",
    width: "25%",
    position: "relative",
    justifyContent: "center",
    alignItems: "center",
  },
  img: {
    height: "100%",
    width: "100%",
  },
  dateOuter: {
    position: "absolute",
    top: "14%",
    width: "86%",
    height: "20px",
    border: "0.7px solid #46A091",
    opacity: 0.7,
  },
  date: {
    position: "absolute",
    top: "15%",
    fontSize: "11px",
    paddingLeft: "15px",
    transform: "rotate(180deg)",
  },
  dateText: {
    position: "absolute",
    top: "16%",
    right: "10%",
    fontSize: "7px",
    fontWeight: "bold",
    transform: "rotate(180deg)",
    color: "#46A091",
  },
  qr: {
    position: "absolute",
    top: "40.5%",
    transform: "rotate(180deg)",
    width: "34px",
    height: "34px",
  },
  qrText: {
    position: "absolute",
    top: "39.95%",
    transform: "rotate(180deg)",
    width: "30.5px",
    fontSize: "3.15px",
    fontWeight: 700,
  },
  dottedeLine: {
    position: "absolute",
    top: "50%",
    width: "100%",
    height: "0px",
    borderTop: "0.7px dashed #46A091",
    opacity: 0.7,
  },
  sizeText: {
    position: "absolute",
    top: "46.5%",
    right: "8%",
    fontSize: "9px",
    fontWeight: "bold",
    transform: "rotate(180deg)",
  },
  barcode: {
    position: "absolute",
    top: "4%",
    left: "7%",
    transform: "rotate(180deg)",
    width: "48%",
    height: "9%",
  },
  contactInfo: {
    position: "absolute",
    top: "4%",
    right: "7%",
    transform: "rotate(180deg)",
    width: "38%",
    height: "9%",
  },
  contactText: {
    width: "86%",
    fontSize: "4.7px",
    fontWeight: 600,
    textAlign: "left",
  },
  infoTitle: {
    position: "absolute",
    top: "33.2%",
    transform: "rotate(180deg)",
    width: "75%",
    fontSize: "7px",
    fontWeight: 700,
    textAlign: "center",
    color: "#46A091",
    fontFamily: "Papyrus",
  },
  infoText: {
    position: "absolute",
    top: "22%",
    transform: "rotate(180deg)",
    width: "75%",
    fontSize: "5.7px",
    fontWeight: 500,
    textAlign: "center",
  },
  mustard: {
    position: "absolute",
    top: "19.5%",
    transform: "rotate(180deg)",
    width: "70%",
    fontSize: "5px",
    fontWeight: 800,
    textAlign: "center",
    color: "#46A091",
  },
  noMustard: {
    position: "absolute",
    top: "18.5%",
    transform: "rotate(180deg)",
    width: "70%",
    fontSize: "4.5px",
    fontWeight: 800,
    textAlign: "center",
    color: "#46A091",
  },
  infoOverlay: {
    position: "absolute",
    top: "80%",
    width: "100%",
    height: "20%",
    justifyContent: "flex-start",
    backgroundColor: "#fff",
    alignItems: "center",
  },
  mainTopText: {
    position: "absolute",
    top: "53.5%",
    fontSize: "8px",
    fontWeight: "bold",
    textAlign: "center",
    width: "80%",
    borderBottom: "1px solid #46A091",
    paddingBottom: "4px",
    color: "#5E5E5E",
  },
  mainTitle: {
    fontSize: "18.5px",
    fontWeight: 700,
    textAlign: "center",
    color: "#5E5E5E",
    fontFamily: "Noteworthy",
  },
  mainText: {
    fontSize: "7px",
    fontWeight: 500,
    textAlign: "center",
    color: "#5E5E5E",
    fontFamily: "Roboto-Italic",
    width: "86%",
  },
  logoOverlay: {
    position: "absolute",
    top: "58%",
    width: "100%",
    height: "22%",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#fff",
  },
  logo: {
    width: "54%",
    marginTop: "10px",
  },
});

export const stylesBox = StyleSheet.create({
  page: {
    flexDirection: "row",
    flexWrap: "wrap",
    backgroundColor: "#fff",
  },
  section: {
    height: "50%",
    width: "25%",
    position: "relative",
    justifyContent: "center",
    alignItems: "center",
  },
  img: {
    height: "100%",
    width: "100%",
  },
  dateOuter: {
    position: "absolute",
    top: "4.5%",
    width: "18%",
    height: "154px",
    border: "0.7px solid #46A091",
    backgroundColor: "#fff",
  },
  date: {
    position: "absolute",
    top: "20%",
    fontSize: "15px",
    transform: "rotate(-90deg)",
  },
  dateText: {
    position: "absolute",
    top: "35.2%",
    right: "45%",
    fontSize: "7px",
    fontWeight: "bold",
    transform: "rotate(-90deg)",
    color: "#519989",
  },
  qr: {
    position: "absolute",
    top: "4.2%",
    left: "4.5%",
    transform: "rotate(-90deg)",
    width: "50px",
    height: "50px",
  },
  barcode: {
    position: "absolute",
    transform: "rotate(-90deg)",
    width: "146px",
    height: "50px",
    top: "17%",
    right: "-26%",
  },
  topDiv: {
    position: "absolute",
    top: "0%",
    width: "100%",
    height: "4%",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#46A091",
  },
  topDivText: {
    color: "#fff",
    fontSize: "5px",
    fontWeight: "bold",
    transform: "rotate(180deg)",
  },
  middleDiv: {
    position: "absolute",
    top: "41.5%",
    width: "100%",
    height: "8%",
    justifyContent: "space-around",
    alignItems: "center",
    flexDirection: "row",
    borderTop: "3px solid #46A091",
    borderBottom: "3px solid #46A091",
  },
  middleDivLogo: {
    height: "80%",
    width: "auto",
    transform: "rotate(-90deg)",
  },
  titleDiv: {
    position: "absolute",
    top: "55.5%",
    width: "190px",
    right: "-14%",
    height: "140px",
    justifyContent: "center",
    alignItems: "flex-start",
    flexDirection: "column",
    backgroundColor: "#fff",
    transform: "rotate(-90deg)",
  },
  titleText: {
    fontSize: "30px",
    fontWeight: "black",
    textAlign: "left",
    color: "#5E5E5E",
    fontFamily: "Roboto-Bold",
  },
  bottomDiv: {
    position: "absolute",
    bottom: "0%",
    width: "100%",
    height: "4%",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#46A091",
  },
  bottomDivText: {
    color: "#fff",
    fontSize: "5px",
    fontWeight: "bold",
  },
  contactDiv: {
    position: "absolute",
    top: "22.5%",
    left: "-8.6%",
    width: "100px",
    height: "54px",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    transform: "rotate(-90deg)",
  },
  contactText: {
    width: "100%",
    fontSize: "6.8px",
    fontWeight: 600,
    textAlign: "left",
  },
});

export const createLabelsPdf = async (labelArr: LabelArrayIF, includeDate: boolean, date: number | null): Promise<Blob> => {
  const tempArr: React.ReactElement[] = [];

  for (let i = 0; i < labelArr.length; i += 1) {
    const entry = labelArr[i];
    const entryDate = includeDate ? ((entry.value as any).labelDate || date) : null;
    const parsedDate = entryDate ? moment(entryDate).format("YYYY-MM-DD") : "";
    const urlString = includeDate && entryDate
      ? `https://vert.farm/farm/product/${entry.value.link}?date=${entryDate}`
      : `https://vert.farm/farm/product/${entry.value.link}`;
    const qrUrl = await generateQrDataUrl(urlString);
    const barcodeUrl = await generateBarcodeDataUrl(entry.value.labelEAN || "");

    for (let j = 0; j < entry.amount; j += 1) {
      const keyBase = `${entry.value.formatedMenuName}-${i}-${j}`;
      if (entry.value.type === "bag") {
        tempArr.push(
          <View style={styles.section} key={keyBase}>
            <View style={styles.dateOuter} /><Text style={styles.dateText}>Skördat:</Text>{parsedDate ? <Text style={styles.date}>{parsedDate}</Text> : null}<View style={styles.dottedeLine} />
            <Text style={styles.sizeText}>{entry.value.labelSize ? `${entry.value.labelSize}g` : ""}</Text><Text style={styles.mainTopText}>Mikrogrönt från VERT.farm</Text><Image style={styles.qr} src={qrUrl} /><Text style={styles.qrText}>SPÅRA INNEHÅLLET</Text><Text style={styles.infoTitle}>Obesprutade grödor med minsta miljöpåverkan</Text><Text style={styles.infoText}>Eftersom all vår odling sker inomhus i en kontrollerad miljö, kan vi eliminera behovet av bekämpningsmedel. Den hydroponiska odlingstekniken vi använder minskar förbrukningen utav vatten med 90%, vilket tillsammans med vår närhet till konsumenterna minskar påverkan på miljön.</Text>
            {entry.value.mustard ? <Text style={styles.mustard}>INNEHÅLLER SENAP</Text> : <Text style={styles.mustard}>KAN INNEHÅLLA SPÅR AV SENAP</Text>}<View style={styles.infoOverlay}><Text style={styles.mainTitle}>{entry.value.name[0]}</Text><Text style={styles.mainText}>{entry.value.subtitle[0]}</Text></View><View style={styles.logoOverlay}>{entry.value.icon ? <Image style={styles.logo} src={entry.value.icon} /> : null}</View>{barcodeUrl ? <Image style={styles.barcode} src={barcodeUrl} /> : null}<View style={styles.contactInfo}><Text style={styles.contactText}>VERT.farm Sverige AB</Text><Text style={styles.contactText}>Björkallén 36</Text><Text style={styles.contactText}>313 33 Oskarström</Text><Text style={styles.contactText}> </Text><Text style={styles.contactText}>admin@vert.farm</Text><Text style={{ ...styles.contactText, fontFamily: "Roboto-Bold" }}>@vert.farm</Text></View>
          </View>,
        );
      } else if (entry.value.type === "box") {
        tempArr.push(
          <View style={stylesBox.section} key={keyBase}>
            <View style={stylesBox.topDiv}><Text style={stylesBox.topDivText}>WWW.VERT.FARM</Text></View><View style={stylesBox.dateOuter} /><Text style={stylesBox.dateText}>Skördat:</Text>{parsedDate ? <Text style={stylesBox.date}>{parsedDate}</Text> : null}<Image style={stylesBox.qr} src={qrUrl} />{barcodeUrl ? <Image style={stylesBox.barcode} src={barcodeUrl} /> : null}<View style={stylesBox.contactDiv}><Text style={stylesBox.contactText}>VERT.farm Sverige AB</Text><Text style={stylesBox.contactText}>Björkallén 36</Text><Text style={stylesBox.contactText}>313 33 Oskarström</Text><Text style={stylesBox.contactText}> </Text><Text style={stylesBox.contactText}>admin@vert.farm</Text><Text style={{ ...stylesBox.contactText, fontFamily: "Roboto-Bold" }}>@vert.farm</Text></View><View style={stylesBox.middleDiv}>{entry.value.icon ? <Image style={stylesBox.middleDivLogo} src={entry.value.icon} /> : null}{entry.value.icon ? <Image style={stylesBox.middleDivLogo} src={entry.value.icon} /> : null}{entry.value.icon ? <Image style={stylesBox.middleDivLogo} src={entry.value.icon} /> : null}</View><View style={stylesBox.titleDiv}><Text style={{ ...stylesBox.titleText, paddingLeft: "0px" }}>VERT.</Text><Text style={{ ...stylesBox.titleText, paddingLeft: "0px" }}>{entry.value.formatedName}</Text><Text style={{ ...stylesBox.titleText, paddingLeft: "0px" }}>{entry.value.formatedSizeString}</Text></View><View style={stylesBox.bottomDiv}><Text style={stylesBox.bottomDivText}>Mikrogrönt odlat i Sverige året runt</Text></View>
          </View>,
        );
      }
    }
  }

  return pdf(<Document><Page size="A4" style={stylesBox.page}>{tempArr}</Page></Document>).toBlob();
};

/**
 * Labels component
 * Manages selected product labels and builds a printable PDF with QR and barcodes.
 */
function Labels() {
  const [user, authLoading] = useAuthState(firebaseAuth);
  const navigate = useNavigate();
  const [labelArr, setLabelArr] = useState<LabelArrayIF>([]);
  const [dateText, setDateText] = useState("");
  const [includeDate, setIncludeDate] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [products, setProducts] = useState<FirestoreFreshProduct[] | null>(null);
  const [packages, setPackages] = useState<FirestoreFreshPackage[] | null>(null);
  const [loadError, setLoadError] = useState("");
  const [reload, setReload] = useState(0);
  const [infoOpen, setInfoOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  useEffect(() => { if (!authLoading && user === null) navigate("/login"); }, [authLoading, user, navigate]);
  useEffect(() => onSnapshot(query(collection(firebaseStore, "products_fresh"), orderBy("name_sv")), (snapshot) => {
    setProducts(snapshot.docs.map((item) => ({ id: item.id, name_sv: item.data().name_sv || "", name_en: item.data().name_en || "", icon: item.data().icon || "", logo: item.data().logo || "", url: item.data().url || "", urlArr: item.data().urlArr || [], subtitle_sv: item.data().subtitle_sv || "" })));
  }, () => setLoadError("Produkterna kunde inte hämtas.")), [reload]);
  useEffect(() => onSnapshot(query(collection(firebaseStore, "products_fresh_package"), orderBy("createdAt", "desc")), (snapshot) => {
    setPackages(snapshot.docs.map((item) => ({ id: item.id, ...item.data() } as FirestoreFreshPackage)));
  }, () => setLoadError("Förpackningarna kunde inte hämtas.")), [reload]);

  const ready = products !== null && packages !== null;
  const catalog = buildMenuArray(products || [], packages || []);
  const options = catalog.filter((item) => !labelArr.some((entry) => entry.id === item.id));
  const total = labelArr.reduce((sum, item) => sum + item.amount, 0);
  const pages = Math.ceil(total / 8);
  const dateValid = moment(dateText, "YYYY-MM-DD", true).isValid();
  const canExport = total > 0 && (!includeDate || dateValid) && !pdfLoading;
  const addLabel = (value: PlantInfoIF) => setLabelArr((current) => current.some((entry) => entry.id === value.id) ? current : [...current, { value, id: value.id, amount: 1 }]);
  const changeAmount = (id: string, amount: number) => {
    if (!Number.isInteger(amount) || amount < 1 || amount > 500) return;
    setLabelArr((current) => current.map((entry) => entry.id === id ? { ...entry, amount } : entry));
  };
  const moveLabel = (index: number, offset: number) => setLabelArr((current) => {
    const result = [...current]; const target = index + offset;
    if (target < 0 || target >= result.length) return current;
    [result[index], result[target]] = [result[target], result[index]]; return result;
  });
  const createPdf = async () => {
    if (!canExport) return;
    setPdfLoading(true); setErrorMessage("");
    try {
      const blob = await createLabelsPdf(labelArr, includeDate, includeDate ? moment(dateText, "YYYY-MM-DD", true).valueOf() : null);
      saveAs(blob, includeDate ? `VERTfarm--${dateText}.pdf` : "VERTfarm.pdf");
      setExportOpen(false);
    } catch (error) { console.error("Could not create label PDF:", error); setErrorMessage("PDF-filen kunde inte skapas. Dina val finns kvar. Försök igen."); }
    finally { setPdfLoading(false); }
  };
  const title = (id: string, text: string, description: string, close: () => void) => <DialogTitle component="div"><div><h2 id={id}>{text}</h2><p className="BatchDialogSubtitle">{description}</p></div><IconButton aria-label="Stäng" disabled={pdfLoading} onClick={close}><CloseIcon /></IconButton></DialogTitle>;

  return <div className="DashBoardTabInner FreshDashboardTab BatchWorkspace LabelWorkspace">
    <div className="FreshTabToolbar"><div><p className="FreshEyebrow">Utskrift</p><h2 className="FreshTabTitle">Etiketter</h2><p className="FreshTabSummary">Välj förpackningar, ange antal och förbered etiketter för utskrift.</p></div><div className="LabelPrimaryActions"><IconButton aria-label="Om Etiketter" title="Om Etiketter" onClick={() => setInfoOpen(true)}><InfoOutlinedIcon /></IconButton><Button color="inherit" variant="contained" startIcon={<LocalPrintshopOutlinedIcon />} disabled={!canExport || !ready || !!loadError} onClick={() => { setErrorMessage(""); setExportOpen(true); }}>Granska utskrift</Button></div></div>
    {loadError ? <Alert severity="error" action={<Button color="inherit" onClick={() => { setLoadError(""); setReload((value) => value + 1); }}>Försök igen</Button>}>{loadError}</Alert> : !ready ? <div className="BatchEmpty" role="status"><CircularProgress size={24} color="inherit" /><p>Hämtar produkter och förpackningar…</p></div> : <>
      <div className="LabelOverview"><div><span>Valda förpackningar</span><strong>{labelArr.length}</strong></div><div><span>Etiketter totalt</span><strong>{total}</strong></div><div><span>A4-ark</span><strong>{pages}<small>8 etiketter per ark</small></strong></div></div>
      <div className="LabelLayout"><section className="LabelBoard" aria-labelledby="label-selection-title"><div className="LabelSectionHeading"><p className="FreshEyebrow">1 · Välj förpackningar</p><h3 id="label-selection-title">Etiketter att skriva ut</h3><p>Påsar och boxar från aktiva förpackningar.</p></div><div className="LabelPicker"><Autocomplete options={options} value={null} getOptionLabel={(option) => `${option.formatedMenuName || option.name[0]} · ${option.type === "box" ? "Box" : "Påse"}`} isOptionEqualToValue={(a,b) => a.id === b.id} onChange={(_, value) => { if (value) addLabel(value); }} disabled={pdfLoading} noOptionsText={catalog.length ? "Alla matchande förpackningar är redan valda" : "Inga aktiva förpackningar finns"} renderOption={(props, option) => <li {...props} key={option.id}>{option.formatedMenuName} · {option.type === "box" ? "Box" : "Påse"}</li>} renderInput={(params) => <TextField {...params} size="small" label="Sök och lägg till förpackning" placeholder="Produkt eller storlek" />} /></div>
        {!labelArr.length ? <div className="BatchEmpty"><LocalPrintshopOutlinedIcon /><strong>{catalog.length ? "Börja med en förpackning" : "Inga förpackningar att välja"}</strong><p>{catalog.length ? "Sök ovan och välj de etiketter du vill skriva ut." : "Skapa eller aktivera en förpackning under Färska förpackningar."}</p></div> : <ol className="LabelSelectionList">{labelArr.map((item, index) => <li className="LabelSelectionRow" key={item.id}><div className="LabelSelectionIdentity"><span className="LabelPosition">{index + 1}</span>{item.value.icon && <img src={item.value.icon} alt="" />}<div><strong>{item.value.formatedMenuName}</strong><span>{item.value.type === "box" ? "Box" : "Påse"} · {item.value.labelEAN ? `EAN ${item.value.labelEAN}` : "Ingen EAN angiven"}</span></div></div><div className="LabelQuantity"><IconButton aria-label={`Minska antal för ${item.value.formatedMenuName}`} disabled={pdfLoading || item.amount <= 1} onClick={() => changeAmount(item.id || "", item.amount - 1)}><RemoveIcon fontSize="small" /></IconButton><TextField size="small" label="Antal" type="number" value={item.amount} inputProps={{ min: 1, max: 500, step: 1, "aria-label": `Antal etiketter för ${item.value.formatedMenuName}` }} disabled={pdfLoading} onChange={(event) => changeAmount(item.id || "", Number(event.target.value))} /><IconButton aria-label={`Öka antal för ${item.value.formatedMenuName}`} disabled={pdfLoading || item.amount >= 500} onClick={() => changeAmount(item.id || "", item.amount + 1)}><AddIcon fontSize="small" /></IconButton></div><div className="LabelRowActions"><IconButton aria-label={`Flytta upp ${item.value.formatedMenuName}`} disabled={pdfLoading || index === 0} onClick={() => moveLabel(index,-1)}><ArrowUpwardIcon fontSize="small" /></IconButton><IconButton aria-label={`Flytta ned ${item.value.formatedMenuName}`} disabled={pdfLoading || index === labelArr.length - 1} onClick={() => moveLabel(index,1)}><ArrowDownwardIcon fontSize="small" /></IconButton><IconButton aria-label={`Ta bort ${item.value.formatedMenuName}`} disabled={pdfLoading} onClick={() => setLabelArr((current) => current.filter((entry) => entry.id !== item.id))}><DeleteIcon fontSize="small" /></IconButton></div></li>)}</ol>}
        <p className="LabelBoardFooter">1–500 etiketter per förpackning. Pilarna ändrar ordningen i PDF-filen.</p>
      </section><aside className="LabelBoard LabelSettings" aria-labelledby="label-settings-title"><div className="LabelSectionHeading"><p className="FreshEyebrow">2 · Utskriftsinställningar</p><h3 id="label-settings-title">Datum & sammanställning</h3></div><div className="LabelSettingsBody"><FormControlLabel control={<Checkbox checked={includeDate} disabled={pdfLoading} onChange={(event) => setIncludeDate(event.target.checked)} />} label="Inkludera skördedatum" /><TextField fullWidth size="small" type="date" label="Skördedatum" value={dateText} onChange={(event) => setDateText(event.target.value)} disabled={!includeDate || pdfLoading} InputLabelProps={{ shrink: true }} error={includeDate && !!dateText && !dateValid} helperText={includeDate ? "Samma datum används för alla valda etiketter och deras QR-koder." : "Etiketter och QR-koder skapas utan skördedatum."} />{includeDate && !dateText && <p className="LabelHint">Välj ett skördedatum för att fortsätta till utskrift.</p>}<dl className="LabelSummary"><div><dt>Etiketter</dt><dd>{total}</dd></div><div><dt>A4-ark</dt><dd>{pages}</dd></div><div><dt>Tomma platser på sista arket</dt><dd>{pages * 8 - total}</dd></div></dl><Button fullWidth color="inherit" variant="contained" startIcon={<LocalPrintshopOutlinedIcon />} disabled={!canExport} onClick={() => { setErrorMessage(""); setExportOpen(true); }}>Granska utskrift</Button><p className="LabelHint">Dina val gäller denna flik och sparas inte när du lämnar den.</p></div></aside></div>
    </>}
    <Dialog className="FreshDialog BatchDialog PlanningDialog LabelDialog" fullWidth maxWidth="sm" open={infoOpen} onClose={() => setInfoOpen(false)} aria-labelledby="label-info-title">{title("label-info-title", "Om Etiketter", "Förbered en PDF med etiketter för påsar och boxar.", () => setInfoOpen(false))}<DialogContent><div className="PlanningForm"><fieldset className="BatchFormSection PlanningFormSection"><legend>Välj & ordna</legend><p className="PlanningHint">Sök efter aktiva förpackningar och ange 1–500 etiketter per rad. Flytta raderna med pilarna för att ändra utskriftsordningen. Borttagna rader går att lägga till igen.</p></fieldset><fieldset className="BatchFormSection PlanningFormSection"><legend>Datum & PDF</legend><p className="PlanningHint">Välj ett gemensamt skördedatum eller avmarkera datum. Datumet används på etiketten och i QR-kodens länk. Granska sammanställningen och hämta sedan PDF-filen.</p><p className="PlanningHint">Ett A4-ark rymmer åtta etiketter. Skriv ut i faktisk storlek (100 %) för att behålla etikettmåtten.</p></fieldset></div></DialogContent><DialogActions><Button color="inherit" variant="contained" onClick={() => setInfoOpen(false)}>Stäng</Button></DialogActions></Dialog>
    <Dialog className="FreshDialog BatchDialog PlanningDialog LabelDialog" fullWidth maxWidth="sm" open={exportOpen} onClose={pdfLoading ? undefined : () => setExportOpen(false)} aria-labelledby="label-export-title">{title("label-export-title", "Granska utskrift", `${total} etiketter på ${pages} A4-ark.`, () => setExportOpen(false))}<DialogContent><div className="PlanningForm"><div className="PlanningPreview"><LocalPrintshopOutlinedIcon /><div><strong>{includeDate ? `Skördedatum ${dateText}` : "Utan skördedatum"}</strong><span>8 etiketter per ark · {pages * 8 - total} tomma platser på sista arket</span></div></div><ol className="LabelExportList">{labelArr.map((item) => <li key={item.id}><div><strong>{item.value.formatedMenuName}</strong><span>{item.value.type === "box" ? "Box" : "Påse"}</span></div><strong>{item.amount} st</strong></li>)}</ol><p className="PlanningHint">PDF-filen följer ordningen ovan. Skriv ut i faktisk storlek (100 %).</p></div></DialogContent>{errorMessage && <Alert severity="error" className="BatchSaveError">{errorMessage}</Alert>}<DialogActions><Button color="inherit" disabled={pdfLoading} onClick={() => setExportOpen(false)}>Tillbaka</Button><Button color="inherit" variant="contained" disabled={!canExport} onClick={createPdf}>{pdfLoading ? "Skapar PDF…" : "Hämta PDF"}</Button></DialogActions></Dialog>
  </div>;
}

export default Labels;
